import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const { FiLayers, FiShield, FiHome, FiTool, FiCheckCircle, FiArrowRight, FiHelpCircle, FiClock, FiDollarSign } = FiIcons;

function Trockenbau() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Trockenbau und Akustikbau",
    "provider": {
      "@type": "HomeAndConstructionBusiness",
      "name": "MK-AUSBAU"
    },
    "description": "Professioneller Trockenbau: Wände einziehen, Decken abhängen, Schallschutz und Brandschutz in Heinsberg.",
    "areaServed": {
      "@type": "City",
      "name": "Heinsberg"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Trockenbau Leistungen",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Trennwand Bau" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Decke abhängen" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Akustikbau" } }
      ]
    }
  };

  const services = [
    {
      title: 'Trennwände und Vorsatzschalen',
      description: 'Flexible Raumaufteilung ohne statische Eingriffe. Ideal für Büroerweiterungen oder neue Kinderzimmer.',
      features: ['Schnelle Montage', 'Hoher Schallschutz möglich', 'Hohlraum für Installationen']
    },
    {
      title: 'Abgehängte Decken',
      description: 'Senken Sie Heizkosten und verbessern Sie die Raumakustik durch moderne Deckensysteme (z.B. Odenwalddecken oder glatte Gipskarton-Decken).',
      features: ['Integrierte LED-Beleuchtung', 'Versteckte Kabelführung', 'Akustikoptimierung']
    },
    {
      title: 'Schallschutz & Akustik',
      description: 'Lärmschutz für mehr Wohnkomfort. Wir entkoppeln Bauteile fachgerecht, um Tritt- und Luftschall zu minimieren.',
      features: ['Entkopplungsprofile', 'Spezial-Gipskarton (z.B. Knauf Silentboard)', 'Mineralwolle-Dämmung']
    },
    {
      title: 'Brandschutzwände (F30-F90)',
      description: 'Zertifizierte Brandschutzlösungen für Ihre Sicherheit. Wir bauen nach DIN 4102.',
      features: ['Feuerschutzplatten', 'Fachgerechte Fugenverspachtelung', 'Revisionsklappen']
    }
  ];

  const faqs = [
    {
      question: "Was kostet eine Trockenbauwand pro qm?",
      answer: "Die Kosten variieren je nach Anforderung (einfach beplankt vs. doppelt, Schallschutz, Brandschutz). Standardwände beginnen oft ab ca. 45-60€ pro m² inkl. Material, bei speziellen Anforderungen entsprechend mehr."
    },
    {
      question: "Ist Trockenbau stabil genug für schwere Hängeschränke?",
      answer: "Ja! Mit der richtigen Planung (Traversen, OSB-Verstärkung oder Hohlraumdübeln) tragen Trockenbauwände auch schwere Lasten wie Küchenschränke oder große TV-Geräte problemlos."
    },
    {
      question: "Wie lange dauert es, eine Zimmerdecke abzuhängen?",
      answer: "Für einen durchschnittlichen Raum (ca. 20m²) benötigen wir in der Regel 2-3 Tage inkl. Unterkonstruktion, Beplankung und Verspachtelung."
    }
  ];

  return (
    <div className="py-20">
      <SEO 
        title="Trockenbau & Akustikbau Heinsberg | Wände & Decken" 
        description="Professioneller Trockenbau in Heinsberg: Trennwände, abgehängte Decken & Schallschutz. Schnell, sauber & preiswert. 🛠️ Jetzt Angebot holen!" 
        keywords="Trockenbau Heinsberg, Decke abhängen, Trennwand ziehen, Akustikbau, Brandschutzwand, Rigips arbeiten"
        schema={serviceSchema}
        canonical="/trockenbau"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-mk-teal-600">Startseite</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-mk-teal-600">Dienstleistungen</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">Trocken- und Akustikbau</span>
        </nav>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiLayers} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Trockenbau & Akustikbau</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Flexible Raumgestaltung, perfekter Schallschutz und schnelle Umsetzung. Wir sind Ihr Partner für professionelle Trockenbauarbeiten in Heinsberg.
          </p>
        </motion.div>

        {/* AI Definition Box - "Featured Snippet" Candidate */}
        <div className="bg-gray-50 border-l-4 border-mk-teal-500 p-6 mb-16 rounded-r-xl">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Was ist Trockenbau?</h2>
          <p className="text-gray-700 leading-relaxed">
            Als <strong>Trockenbau</strong> bezeichnet man die Errichtung von raumbegrenzenden Bauteilen (Wände, Decken, Böden) durch das Zusammenfügen industrieller Halbzeuge. Im Gegensatz zum Massivbau werden hier keine wasserhaltigen Baustoffe wie Beton oder Putz zur Konstruktion verwendet. Dies ermöglicht eine <strong>schnellere Bauzeit</strong> (keine Trocknungszeiten) und eine <strong>flexiblere Raumaufteilung</strong>.
          </p>
        </div>

        {/* Hero Image */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <img 
            src="https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg" 
            alt="Professionelle Trockenbauarbeiten: Abgehängte Decke mit Beleuchtung" 
            className="w-full h-96 object-cover rounded-2xl shadow-2xl"
          />
        </motion.div>

        {/* Services Grid */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Unsere Trockenbau-Leistungen im Detail</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg border border-gray-100"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-mk-teal-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Section - How we work */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Ablauf Ihres Projekts</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Beratung', description: 'Aufmaß vor Ort & Materialberatung' },
              { step: '02', title: 'Planung', description: 'Transparenter Kostenvoranschlag' },
              { step: '03', title: 'Ausführung', description: 'Ständerwerk, Beplankung, Spachteln (Q1-Q4)' },
              { step: '04', title: 'Abnahme', description: 'Besenreine Übergabe & Qualitätscheck' }
            ].map((item, index) => (
              <motion.div 
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-red-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-20 bg-mk-teal-50 p-8 rounded-2xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center flex items-center justify-center">
            <SafeIcon icon={FiHelpCircle} className="mr-3 text-mk-teal-600" />
            Häufige Fragen zum Trockenbau
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">{faq.question}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white p-12 rounded-xl shadow-2xl">
          <h2 className="text-3xl font-bold mb-4">Planen Sie einen Umbau?</h2>
          <p className="text-xl text-mk-teal-100 mb-8 max-w-2xl mx-auto">
            Wir beraten Sie gerne zu Schallschutz, Raumaufteilung und Kosten. Unverbindlich und ehrlich.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center space-x-2 bg-white text-mk-teal-600 px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              <span>Jetzt Angebot anfordern</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
      
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default Trockenbau;